import { computed } from "vue";
import DraggerMenuState, {
  defaultHandleScaleIndex,
} from "@/stores/DraggerMenuState";
import Settings, {
  DEFAULT_CANVAS_UI_SIZE,
  type CanvasUiSize,
} from "@/stores/Settings";
import {
  setToolbarColumnsMode,
  showAllTools,
  toolbarColumnsMode,
} from "@/stores/ToolbarVisibilityState";
import { rulerSize, setRulerSize } from "@/stores/RulerState";
import {
  DEFAULT_RULER_SIZE,
  type RulerSize,
} from "@/drawing/overlay/rulerSizes";
import type { ToolbarColumnsMode } from "@/utils/toolbar-layout";
import type { PlanBackground } from "@/utils/planBackground";
import { setPlanBackground } from "@/stores/PlanBackgroundState";
import { useToast } from "@/composable/useToast";

/**
 * Réglages de la modale Affichage (ex-réglages du volet) : tailles des icônes
 * et des textes, taille des poignées, épaisseur des règles, aides
 * contextuelles et colonnes de la barre d'outils. Chacun
 * garde son store, sa persistance et son suivi d'origine.
 */
export function useDisplaySettings() {
  const canvasIconSize = computed({
    get: () => Settings.canvasIconSize,
    set: (size: CanvasUiSize) => {
      if (size !== Settings.canvasIconSize) Settings.setCanvasIconSize(size);
    },
  });

  const canvasTextSize = computed({
    get: () => Settings.canvasTextSize,
    set: (size: CanvasUiSize) => {
      if (size !== Settings.canvasTextSize) Settings.setCanvasTextSize(size);
    },
  });

  // Fond du plan : réglage des paramètres généraux, appliqué en direct. Pas
  // remis par « Rétablir par défaut » (réglage global, et le plan doit être
  // redessiné).
  const { showDanger } = useToast();
  const planBackground = computed({
    get: () => Settings.actviveTheme.name,
    set: (name: PlanBackground) => {
      void setPlanBackground(name).then((result) => {
        if (result === "failed") {
          showDanger("canvas:display.planBackground.failed");
        }
      });
    },
  });

  const handlesScale = computed({
    get: () => DraggerMenuState.handlesScale,
    set: (value: number) => DraggerMenuState.setHandlesScale(value),
  });

  const rulerThickness = computed({
    get: () => rulerSize.value,
    set: (size: RulerSize) => setRulerSize(size),
  });

  const toolbarColumns = computed({
    get: () => toolbarColumnsMode.value,
    set: (mode: ToolbarColumnsMode) => setToolbarColumnsMode(mode),
  });

  const toolHintsOn = computed({
    get: () => Settings.toolHintsOn,
    set: (on: boolean) => {
      if (on !== Settings.toolHintsOn) Settings.toggleToolHints();
    },
  });

  /** « Rétablir par défaut » : tous les réglages et tous les outils. */
  function resetDisplay() {
    canvasIconSize.value = DEFAULT_CANVAS_UI_SIZE;
    canvasTextSize.value = DEFAULT_CANVAS_UI_SIZE;
    if (handlesScale.value !== defaultHandleScaleIndex) {
      handlesScale.value = defaultHandleScaleIndex;
    }
    rulerThickness.value = DEFAULT_RULER_SIZE;
    toolHintsOn.value = true;
    toolbarColumns.value = "auto";
    showAllTools();
  }

  return {
    canvasIconSize,
    canvasTextSize,
    planBackground,
    handlesScale,
    rulerThickness,
    toolHintsOn,
    toolbarColumns,
    resetDisplay,
  };
}
